/**
 * Global constants: timing and field geometry (ARCHITECTURE §4, §5).
 *
 * World units: 1 unit = 1 yard. x runs along the field, y points UP, ground at y = 0.
 * The field is 120 yd long including both end zones: x ∈ [FIELD_MIN_X, FIELD_MAX_X].
 * The LEFT goal line is at x = 0 and the RIGHT goal line at x = 100.
 *
 * Direction convention: an offense with `attackDir = +1` attacks the RIGHT end zone (scores past x = 100);
 * `attackDir = -1` attacks the LEFT end zone (scores past x = 0). The rules state stores `attackDir` per possession.
 */

// ── Timing ─────────────────────────────────────────────────────────────────────────────────────────────
/** Simulation rate. One `step` = one tick. */
export const TICK_HZ = 60;
/** Seconds per tick (exact IEEE value of 1/60, used by the sim for per-second tuning values). */
export const DT = 1 / TICK_HZ;
/** Milliseconds per tick (for the client's fixed-step accumulator). */
export const TICK_MS = 1000 / TICK_HZ;
/** Convert seconds to whole ticks (rounded). Use at module init only, not inside the hot loop. */
export const secondsToTicks = (s: number): number => Math.round(s * TICK_HZ);

// ── Field ──────────────────────────────────────────────────────────────────────────────────────────────
export const FIELD_MIN_X = -10;
export const FIELD_MAX_X = 110;
export const FIELD_LENGTH = FIELD_MAX_X - FIELD_MIN_X; // 120
/** Left goal line (the left end zone spans [-10, 0]). */
export const GOAL_LINE_LEFT = 0;
/** Right goal line (the right end zone spans [100, 110]). */
export const GOAL_LINE_RIGHT = 100;
export const ENDZONE_DEPTH = 10;
/** Back line of the left end zone (goalpost plane for tosses at the left end). */
export const END_LINE_LEFT = FIELD_MIN_X;
/** Back line of the right end zone (goalpost plane for tosses at the right end). */
export const END_LINE_RIGHT = FIELD_MAX_X;
export const MIDFIELD_X = 50;
export const GROUND_Y = 0;

/**
 * Soft play boundary: entities are clamped to [PLAY_MIN_X, PLAY_MAX_X] (a little past the end lines) so nobody
 * can run off into the void. There are no walls; the camera follows the play.
 */
export const PLAY_MIN_X = FIELD_MIN_X - 4;
export const PLAY_MAX_X = FIELD_MAX_X + 4;

// ── Goalposts (side view) ──────────────────────────────────────────────────────────────────────────────
// Seen from the side, the crossbar is a point (it runs into the screen) and the two uprights overlap into a
// single vertical line above it. A toss is GOOD when the ball crosses the end-line plane strictly between
// GOALPOST_CROSSBAR_Y and GOALPOST_UPRIGHT_TOP_Y while travelling toward the end line.
/** Crossbar height (10 ft). */
export const GOALPOST_CROSSBAR_Y = 10 / 3;
/** Top of the uprights (35 ft). Anything above is NO GOOD ("over the top" — we are stricter than the NFL). */
export const GOALPOST_UPRIGHT_TOP_Y = 35 / 3;
/** Collision radius of the crossbar knob (a circle at (endLine, CROSSBAR_Y)). DOINK. */
export const GOALPOST_CROSSBAR_RADIUS = 0.15;
/** Collision radius of the upright tips (a circle at (endLine, UPRIGHT_TOP_Y)). */
export const GOALPOST_UPRIGHT_TIP_RADIUS = 0.12;
/** The stanchion is a vertical segment x = endLine, y ∈ [0, CROSSBAR_Y], with this half-thickness. */
export const GOALPOST_STANCHION_HALF_WIDTH = 0.12;

// ── Bodies ─────────────────────────────────────────────────────────────────────────────────────────────
/** Base head radius before character multipliers (ranked always uses this). */
export const HEAD_RADIUS = 0.9;
/** Football ellipse semi-axes (full size ≈ 0.55 × 0.34 yd). */
export const BALL_HALF_LENGTH = 0.275;
export const BALL_HALF_HEIGHT = 0.17;
/** Bounding radius of the ball (used for broad-phase and simple circle tests). */
export const BALL_RADIUS = BALL_HALF_LENGTH;
/** Glove collision radius. */
export const GLOVE_RADIUS = 0.38;

// ── Football ───────────────────────────────────────────────────────────────────────────────────────────
export const DOWNS_PER_SERIES = 4;
export const YARDS_FOR_FIRST_DOWN = 10;
/** Drives format: each possession starts at the OPPONENT'S 40 (40 yd from the goal line being attacked). */
export const DRIVE_START_YARDS_TO_GOAL = 40;
/** Overtime possessions start at the opponent's 25. */
export const OVERTIME_START_YARDS_TO_GOAL = 25;
export const DRIVES_PER_PLAYER = 4;
export const OFFSIDE_PENALTY_YARDS = 5;
/** Two-point conversion: one play from the 3. */
export const TWO_POINT_YARDS_TO_GOAL = 3;
/** One-point toss: a field-goal-style throw from the 15. */
export const PAT_TOSS_YARDS_TO_GOAL = 15;
// ── Classic format: kicks and field position (ARCHITECTURE §5 "Classic format") ───────────────────────────
/** Kickoff toss: from the kicking player's own 35 (65 yd from the goal line he attacks). */
export const KICKOFF_YARDS_TO_GOAL = 65;
/** Free kick after a safety: the conceding player kicks from his own 20. */
export const SAFETY_KICK_YARDS_TO_GOAL = 80;
/** Kickoff touchback: the receiver starts at his own 25. */
export const KICKOFF_TOUCHBACK_YARDS_TO_GOAL = 75;
/** Punt touchback, interception touchback and the deepest spot after a missed field goal: own 20. */
export const TOUCHBACK_YARDS_TO_GOAL = 80;
/** A kickoff / onside toss is a free ball for the KICKING player only once it has gone this far past the LOS. */
export const ONSIDE_MIN_YARDS = 10;
/** Neutral gap between the two heads at the line of scrimmage (center to LOS, each side). */
export const LINEUP_GAP = 1.6;

export const POINTS_TOUCHDOWN = 6;
export const POINTS_FIELD_GOAL = 3;
export const POINTS_SAFETY = 2;
export const POINTS_PAT_TOSS = 1;
export const POINTS_TWO_POINT = 2;

/** Max players in a match (1v1). Fixed-size arrays in SimState use this. */
export const PLAYER_COUNT = 2;

// ── Yard helpers (pure, allocation-free) ───────────────────────────────────────────────────────────────
/** x coordinate of the goal line that an offense with `attackDir` is attacking. */
export const goalLineFor = (attackDir: number): number => (attackDir > 0 ? GOAL_LINE_RIGHT : GOAL_LINE_LEFT);
/** x coordinate of the end line (goalpost plane) that an offense with `attackDir` is attacking. */
export const endLineFor = (attackDir: number): number => (attackDir > 0 ? END_LINE_RIGHT : END_LINE_LEFT);
/** Yards remaining to the attacked goal line from `x` (negative once in the end zone). */
export const yardsToGoal = (x: number, attackDir: number): number =>
  attackDir > 0 ? GOAL_LINE_RIGHT - x : x - GOAL_LINE_LEFT;
/** x for a spot `yards` away from the attacked goal line. */
export const xFromYardsToGoal = (yards: number, attackDir: number): number =>
  attackDir > 0 ? GOAL_LINE_RIGHT - yards : GOAL_LINE_LEFT + yards;
/**
 * Broadcast-style yard-line label (0–50) for an x coordinate, e.g. 60 → 40, 25 → 25. Values in the end zones
 * clamp to 0. Use for HUD text ("OPP 40").
 */
export const yardLineLabel = (x: number): number => {
  const fromLeft = Math.min(Math.max(x, GOAL_LINE_LEFT), GOAL_LINE_RIGHT);
  return Math.round(fromLeft <= MIDFIELD_X ? fromLeft : GOAL_LINE_RIGHT - fromLeft);
};
